import OutlineTitle from "@/components/OutlineTitle";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { pointsAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Trophy, Coins, Gift, Medal, Copy, CheckCircle2, XCircle, Clock } from "lucide-react";

const rankColor = (r) => (r === 1 ? "#FFD700" : r === 2 ? "#C0C0C0" : r === 3 ? "#CD7F32" : "hsl(var(--muted-foreground))");

const statusStyle = {
  pending: { icon: Clock, label: "Pending", cls: "bg-amber-500/15 text-amber-500" },
  approved: { icon: CheckCircle2, label: "Fulfilled", cls: "bg-emerald-500/15 text-emerald-500" },
  rejected: { icon: XCircle, label: "Rejected", cls: "bg-destructive/15 text-destructive" },
};

export default function PointsLeaderboard() {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTab = params.get("tab") === "redemptions" ? "redemptions" : "leaderboard";
  const [balance, setBalance] = useState(null);
  const [board, setBoard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [history, setHistory] = useState([]);
  const [options, setOptions] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      pointsAPI.getBalance().catch(() => ({ data: {} })),
      pointsAPI.getLeaderboard(50).catch(() => ({ data: {} })),
      pointsAPI.getHistory({ limit: 20 }).catch(() => ({ data: {} })),
      pointsAPI.getRedemptionOptions().catch(() => ({ data: {} })),
      pointsAPI.getMyRedemptions({ limit: 30 }).catch(() => ({ data: {} })),
    ]).then(([b, l, h, o, r]) => {
      setBalance(b.data?.balance ?? 0);
      setBoard(l.data?.leaderboard || []);
      setMyRank(l.data?.current_user_rank);
      setHistory(h.data?.history || h.data?.transactions || []);
      setOptions(o.data?.options || o.data?.redemption_options || (Array.isArray(o.data) ? o.data : []));
      setRedemptions(r.data?.requests || []);
      setLoading(false);
    });
  };
  useEffect(() => load(), []);

  const redeem = async (id) => {
    try {
      await pointsAPI.submitRedemption({ option_id: id });
      toast.success("Redemption submitted for approval");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not redeem");
    }
  };

  const copyNote = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const pendingCount = redemptions.filter((r) => r.status === "pending").length;
  const unseenApproved = redemptions.filter((r) => r.status === "approved" && r.admin_notes).length;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <OutlineTitle>Points Portal</OutlineTitle>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-border bg-gradient-to-br from-primary/20 to-card p-5">
          <Coins className="mb-2 h-6 w-6 text-accent" />
          <p className="font-display text-3xl font-bold">{loading ? "—" : balance}</p>
          <p className="text-xs text-muted-foreground">Your points</p>
        </Card>
        <Card className="border-border bg-card p-5">
          <Trophy className="mb-2 h-6 w-6 text-accent" />
          <p className="font-display text-3xl font-bold">{myRank ? `#${myRank}` : "—"}</p>
          <p className="text-xs text-muted-foreground">Your rank</p>
        </Card>
        <Card className="border-border bg-card p-5">
          <Medal className="mb-2 h-6 w-6 text-primary" />
          <p className="font-display text-3xl font-bold">{board.length}</p>
          <p className="text-xs text-muted-foreground">Ranked racers</p>
        </Card>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList>
          <TabsTrigger value="leaderboard" data-testid="points-tab-leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="points-tab-rewards">Rewards</TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="points-tab-redemptions">
            My Redemptions
            {(pendingCount + unseenApproved) > 0 && (
              <span className="ml-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
                {pendingCount + unseenApproved}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="points-tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="mt-4">
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
          ) : (
            <Card className="divide-y divide-border border-border bg-card">
              {board.map((r) => (
                <div key={r.user_id} className={`flex items-center gap-3 p-3 ${r.user_id === user?.id ? "bg-primary/10" : ""}`} data-testid={`leaderboard-row-${r.rank}`}>
                  <span className="w-8 text-center font-display text-lg font-bold" style={{ color: rankColor(r.rank) }}>{r.rank}</span>
                  <Avatar className="h-9 w-9"><AvatarImage src={r.profile_image} /><AvatarFallback className="bg-secondary text-xs">{r.username.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <span className="flex-1 font-medium">{r.username}{r.user_id === user?.id && <span className="ml-2 text-xs text-primary">You</span>}</span>
                  <span className="flex items-center gap-1 font-display font-bold text-accent"><Coins className="h-4 w-4" /> {r.points}</span>
                </div>
              ))}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rewards" className="mt-4">
          {options.length ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {options.map((o) => (
                <Card key={o.id} className="border-border bg-card p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{o.name || o.title}</p>
                      <p className="text-sm text-muted-foreground">{o.description}</p>
                    </div>
                    <Badge className="border-0 bg-accent/20 text-accent">{o.points_cost || o.cost} pts</Badge>
                  </div>
                  <Button size="sm" className="mt-3 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => redeem(o.id)} data-testid={`redeem-${o.id}`}>
                    <Gift className="mr-1 h-4 w-4" /> Redeem
                  </Button>
                </Card>
              ))}
            </div>
          ) : <p className="py-10 text-center text-sm text-muted-foreground">No rewards available right now.</p>}
        </TabsContent>

        <TabsContent value="redemptions" className="mt-4">
          {redemptions.length ? (
            <div className="space-y-3">
              {redemptions.map((r) => {
                const s = statusStyle[r.status] || statusStyle.pending;
                const Icon = s.icon;
                return (
                  <Card key={r.id} className="border-border bg-card p-4" data-testid={`user-redemption-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{r.option_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Requested {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                        </p>
                      </div>
                      <Badge className={`border-0 ${s.cls}`}>
                        <Icon className="mr-1 h-3 w-3" /> {s.label}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <Coins className="h-4 w-4 text-accent" />
                      <span className="font-bold text-accent">{r.points_cost} pts</span>
                    </div>
                    {r.admin_notes && (
                      <div className="mt-3 rounded-md border border-border bg-muted/40 p-3">
                        <div className="mb-1 flex items-center justify-between">
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Note from admin{r.status === "approved" ? " (your code/details)" : ""}
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => copyNote(r.admin_notes)}
                            data-testid={`copy-note-${r.id}`}
                          >
                            <Copy className="mr-1 h-3 w-3" /> Copy
                          </Button>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm">{r.admin_notes}</p>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">You haven&apos;t redeemed anything yet.</p>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length ? (
            <Card className="divide-y divide-border border-border bg-card">
              {history.map((h, i) => (
                <div key={h.id || i} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <p className="font-medium">{h.reason || h.description || h.action}</p>
                    <p className="text-xs text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}</p>
                  </div>
                  <span className={`font-bold ${(h.points || h.amount) >= 0 ? "text-accent" : "text-destructive"}`}>{(h.points || h.amount) >= 0 ? "+" : ""}{h.points ?? h.amount}</span>
                </div>
              ))}
            </Card>
          ) : <p className="py-10 text-center text-sm text-muted-foreground">No points history yet.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
